import React, { useCallback, useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Split, Plus, Trash2, Pencil, FlaskConical } from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "@/lib/apiClient";

const chf = (v) => Number(v || 0).toLocaleString("de-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const EMPTY = { name: "", supplier_contains: "", text_contains: "", parts: [{ account: "", percent: 80, label: "" }, { account: "", percent: 20, label: "" }], active: true };

function RuleForm({ initial, accounts, onSave, onCancel }) {
  const [f, setF] = useState(initial);
  const sum = f.parts.reduce((a, p) => a + Number(p.percent || 0), 0);
  const upd = (i, k, v) => setF({ ...f, parts: f.parts.map((p, j) => (j === i ? { ...p, [k]: v } : p)) });
  return (
    <div className="rounded-md border border-slate-200 p-3 space-y-2 bg-slate-50/50" data-testid="split-rule-form">
      <div className="grid md:grid-cols-3 gap-2">
        <Input className="h-8 text-sm" placeholder="Name der Regel (z. B. Telefon privat/geschäftlich)" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="split-rule-name" />
        <Input className="h-8 text-sm" placeholder="Lieferant enthält … (z. B. Swisscom)" value={f.supplier_contains || ""} onChange={(e) => setF({ ...f, supplier_contains: e.target.value })} data-testid="split-rule-supplier" />
        <Input className="h-8 text-sm" placeholder="oder Stichwort im Beleg (z. B. Mobile Abo)" value={f.text_contains || ""} onChange={(e) => setF({ ...f, text_contains: e.target.value })} data-testid="split-rule-text" />
      </div>
      <table className="w-full text-xs">
        <thead><tr className="text-left text-slate-500"><th>Konto</th><th>Anteil %</th><th>Bezeichnung (optional)</th><th /></tr></thead>
        <tbody>
          {f.parts.map((p, i) => (
            <tr key={i} data-testid={`split-rule-part-${i}`}>
              <td className="pr-1 py-0.5">
                <select className="h-8 w-full text-xs border border-slate-200 rounded px-1 bg-white" value={p.account} onChange={(e) => upd(i, "account", e.target.value)} data-testid={`split-rule-part-account-${i}`}>
                  <option value="">Konto wählen…</option>
                  {accounts.map((a) => <option key={a._id || a.id || a.code} value={a.code}>{a.code} {a.name}</option>)}
                </select>
              </td>
              <td className="pr-1 py-0.5"><Input className="h-8 w-20 text-xs" type="number" step="0.5" value={p.percent} onChange={(e) => upd(i, "percent", Number(e.target.value))} data-testid={`split-rule-part-percent-${i}`} /></td>
              <td className="pr-1 py-0.5"><Input className="h-8 text-xs" value={p.label || ""} onChange={(e) => upd(i, "label", e.target.value)} placeholder="z. B. Privatanteil" data-testid={`split-rule-part-label-${i}`} /></td>
              <td className="py-0.5">{f.parts.length > 2 && <button type="button" className="text-slate-400 hover:text-rose-600" onClick={() => setF({ ...f, parts: f.parts.filter((_, j) => j !== i) })} data-testid={`split-rule-part-remove-${i}`}><Trash2 className="h-3.5 w-3.5" /></button>}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-3 text-xs">
          <button type="button" className="text-slate-600 underline" onClick={() => setF({ ...f, parts: [...f.parts, { account: "", percent: 0, label: "" }] })} data-testid="split-rule-part-add">+ Anteil</button>
          <span className={sum === 100 ? "text-emerald-700" : "text-rose-700"} data-testid="split-rule-sum">Summe {sum} %</span>
          <label className="inline-flex items-center gap-1"><input type="checkbox" checked={f.active !== false} onChange={(e) => setF({ ...f, active: e.target.checked })} data-testid="split-rule-active" />aktiv</label>
        </div>
        <div className="flex gap-2">
          <Button type="button" size="sm" variant="ghost" onClick={onCancel} data-testid="split-rule-cancel">Abbrechen</Button>
          <Button type="button" size="sm" onClick={() => onSave(f)} disabled={sum !== 100 || !f.name} data-testid="split-rule-save">Speichern</Button>
        </div>
      </div>
    </div>
  );
}

export default function SplitRulesCard({ companyId }) {
  const [rules, setRules] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [editing, setEditing] = useState(null); // null | "new" | rule
  const [test, setTest] = useState({ supplier: "", text: "", amount: 100 });
  const [preview, setPreview] = useState(null);

  const load = useCallback(async () => {
    if (!companyId) return;
    try {
      const [r, a] = await Promise.all([apiClient.get(`/companies/${companyId}/split-rules`), apiClient.get(`/companies/${companyId}/accounts`)]);
      setRules(r.data || []);
      setAccounts((a.data || []).filter((x) => ["expense", "income", "asset", "liability", "equity"].includes(x.type)).sort((x, y) => String(x.code).localeCompare(String(y.code))));
    } catch { /* ignore */ }
  }, [companyId]);
  useEffect(() => { load(); }, [load]);

  const save = async (f) => {
    const body = { ...f, supplier_contains: f.supplier_contains || null, text_contains: f.text_contains || null, parts: f.parts.map((p) => ({ account: p.account, percent: Number(p.percent), label: p.label || null })) };
    try {
      if (editing === "new") await apiClient.post(`/companies/${companyId}/split-rules`, body);
      else await apiClient.put(`/companies/${companyId}/split-rules/${editing.id}`, body);
      toast.success("Split-Regel gespeichert"); setEditing(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen"); }
  };
  const remove = async (r) => {
    if (!window.confirm(`Regel «${r.name}» löschen?`)) return;
    try { await apiClient.delete(`/companies/${companyId}/split-rules/${r.id}`); toast.success("Gelöscht"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  };
  const runPreview = async () => {
    try { const r = await apiClient.post(`/companies/${companyId}/split-rules/preview`, { ...test, amount: Number(test.amount) }); setPreview(r.data); } catch { setPreview(null); }
  };

  return (
    <Card className="p-5 border border-slate-200 bg-white" data-testid="split-rules-card">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-start gap-2">
          <Split className="h-4 w-4 text-slate-700 mt-0.5" />
          <div>
            <h3 className="text-sm font-semibold text-slate-900">Split-Regeln für Belege</h3>
            <p className="text-xs text-slate-500">Belege eines Lieferanten oder mit Stichwort werden im Posteingang automatisch prozentual auf mehrere Konten aufgeteilt – z. B. Telefon 80 % Geschäftsaufwand / 20 % Privatanteil. Die Aufteilung erscheint als Vorschlag beim Beleg und wird beim Buchen auf den Nettobetrag angewendet.</p>
          </div>
        </div>
        {editing === null && <Button type="button" size="sm" onClick={() => setEditing("new")} data-testid="split-rule-add"><Plus className="h-4 w-4 mr-1" />Regel</Button>}
      </div>
      {editing !== null && <RuleForm initial={editing === "new" ? EMPTY : { ...editing }} accounts={accounts} onSave={save} onCancel={() => setEditing(null)} />}
      <table className="w-full text-sm mt-3" data-testid="split-rules-table">
        <thead><tr className="text-xs text-slate-500 border-b border-slate-200 text-left"><th className="py-1">Regel</th><th>Trifft zu bei</th><th>Aufteilung</th><th /></tr></thead>
        <tbody>
          {rules.map((r) => (
            <tr key={r.id} className={`border-b border-slate-100 ${r.active === false ? "opacity-50" : ""}`} data-testid={`split-rule-row-${r.id}`}>
              <td className="py-1.5 font-medium text-slate-800">{r.name}{r.active === false && <span className="ml-1 text-[10px] text-slate-400">inaktiv</span>}</td>
              <td className="text-xs text-slate-600">{[r.supplier_contains && `Lieferant «${r.supplier_contains}»`, r.text_contains && `Text «${r.text_contains}»`].filter(Boolean).join(" · ")}</td>
              <td className="text-xs text-slate-700">{r.parts.map((p) => `${p.percent}% → ${p.account}${p.label ? ` (${p.label})` : ""}`).join(" · ")}</td>
              <td className="text-right whitespace-nowrap">
                <button type="button" className="text-slate-400 hover:text-slate-800 mr-2" onClick={() => setEditing(r)} data-testid={`split-rule-edit-${r.id}`}><Pencil className="h-3.5 w-3.5" /></button>
                <button type="button" className="text-slate-400 hover:text-rose-600" onClick={() => remove(r)} data-testid={`split-rule-delete-${r.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
              </td>
            </tr>
          ))}
          {!rules.length && <tr><td colSpan={4} className="py-3 text-xs text-slate-400" data-testid="split-rules-empty">Noch keine Split-Regel – z. B. «Swisscom» 80 % 6510 / 20 % 2850.</td></tr>}
        </tbody>
      </table>
      <div className="mt-4 rounded-md border border-dashed border-slate-200 p-3" data-testid="split-rules-test">
        <div className="text-xs font-medium text-slate-700 flex items-center gap-1 mb-2"><FlaskConical className="h-3.5 w-3.5" />Regel testen</div>
        <div className="flex flex-wrap gap-2 items-center">
          <Input className="h-8 w-44 text-xs" placeholder="Lieferant" value={test.supplier} onChange={(e) => setTest({ ...test, supplier: e.target.value })} data-testid="split-test-supplier" />
          <Input className="h-8 w-44 text-xs" placeholder="Belegtext" value={test.text} onChange={(e) => setTest({ ...test, text: e.target.value })} data-testid="split-test-text" />
          <Input className="h-8 w-28 text-xs" type="number" value={test.amount} onChange={(e) => setTest({ ...test, amount: e.target.value })} data-testid="split-test-amount" />
          <Button type="button" size="sm" variant="outline" className="h-8" onClick={runPreview} data-testid="split-test-run">Testen</Button>
        </div>
        {preview && (
          <div className="mt-2 text-xs" data-testid="split-test-result">
            {preview.matched ? (<>
              <div className="text-emerald-700">Regel «{preview.rule.name}» greift:</div>
              {preview.split.lines.map((l, i) => <div key={i} className="text-slate-700">{l.percent}% → {l.account_label}: <b>CHF {chf(l.amount)}</b></div>)}
            </>) : <div className="text-slate-500">Keine Regel passt.</div>}
          </div>
        )}
      </div>
    </Card>
  );
}
